import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  AlertTypes,
  LayoutNames,
  NewWorkflowWidgetConfig,
  WidgetNames,
  WorkflowPageConfig,
  findWidget,
  getWidgetsArrayByLayoutTypeOrWidgetId,
  is2dArray,
} from '@finxone-platform/shared/sys-config-types';
import { MIN_MULTI_COLUMN_COUNT, containerConfigName } from '@finxone-platform/shared/workflow-mapping';

export type ColumnCountChange = {
  uniqueId: string;
  selectedNoOfColumn: number;
  selectedColumnRange: number[];
  childComponentOuterCss?: string[];
};

/**
 * Removes a single column from a multi column widget along with the widgets it holds, keeping the widget
 * properties that drive the layout (column configs, column count and split ranges) in step. Mutates the
 * given page config in place.
 *
 * Returns the resulting column count change, or null when nothing was removed.
 */
export function removeColumnFromMultiColumnWidget(
  pageConfig: WorkflowPageConfig | undefined,
  layoutWidgetUniqueId: string | number,
  columnIndex: number,
  widgetLayoutType: LayoutNames | undefined,
  alertHandlerService: AlertHandlerService,
): ColumnCountChange | null {
  const widgets = getWidgetsArrayByLayoutTypeOrWidgetId(pageConfig, widgetLayoutType, layoutWidgetUniqueId);
  const widget = findWidget(layoutWidgetUniqueId, widgets);

  if (!widget || !is2dArray(widget.childComponents)) {
    alertHandlerService.showAlertFn(AlertTypes.WARNING, 'No widget found to delete column');
    return null;
  }

  const columns = widget.childComponents as NewWorkflowWidgetConfig[][];

  if (columns.length <= MIN_MULTI_COLUMN_COUNT) {
    alertHandlerService.showAlertFn(
      AlertTypes.WARNING,
      `A multi column widget must keep at least ${MIN_MULTI_COLUMN_COUNT} columns.`,
    );
    return null;
  }

  if (columnIndex < 0 || columnIndex >= columns.length) {
    alertHandlerService.showAlertFn(AlertTypes.WARNING, 'No column found to delete');
    return null;
  }

  columns.splice(columnIndex, 1);

  const attributes = widget.widgetConfiguration.attributes;
  const widgetProperties = attributes.widgetProperties ?? {};
  const columnConfigs = widgetProperties[containerConfigName[WidgetNames.TWO_COLUMN] as string];

  if (Array.isArray(columnConfigs)) {
    columnConfigs.splice(columnIndex, 1);
  }

  widgetProperties['selectedNoOfColumn'] = columns.length;

  if (widgetProperties['selectedSplitType'] === 'custom') {
    if (Array.isArray(widgetProperties['selectedColumnRange'])) {
      widgetProperties['selectedColumnRange'].splice(columnIndex, 1);
    }
  } else {
    widgetProperties['selectedColumnRange'] = new Array(columns.length).fill(100 / columns.length);
  }

  attributes.widgetProperties = widgetProperties;

  const selectedColumnRange = widgetProperties['selectedColumnRange'];

  return {
    uniqueId: layoutWidgetUniqueId.toString(),
    selectedNoOfColumn: columns.length,
    selectedColumnRange: Array.isArray(selectedColumnRange) ? [...selectedColumnRange] : [],
  };
}
